// apps/web/src/wizard/A7ConfirmationPage.tsx — Phase 4 plan 04-03.
//
// Reservation confirmation page (A7). Reads the createReservation
// response from `useLocation().state` (NOT the query string —
// OQ-7: the token is sensitive and must never appear in the
// URL bar). On first render the page calls
// `history.replaceState(null, '', '/reservierung/bestaetigung')` to
// scrub any query parameters (defense in depth).
//
// Renders: the reservation number (R-YYMMDD-#### for auto, A-…
// for manual per D-05), the self-service link (the ONLY place the
// token appears in the DOM), the 24-hour validity caption, and two
// next-action buttons. If `state` is null (the user reloaded the
// page), the fallback message + "Zur Startseite" link renders.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Copy, ExternalLink, Home, RotateCcw } from "lucide-react";
import { resetWizard } from "./state";

interface A7State {
  reservationNumber?: string;
  selfServiceToken?: string;
  selfServiceUrl?: string;
  expiresAt?: string;
  confirmationMode?: "auto" | "manual";
}

export function A7ConfirmationPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as A7State | null;
  const [copied, setCopied] = useState(false);

  // Defense in depth: scrub any query parameters from the URL bar.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.search.length > 0 || window.location.hash.length > 0) {
      window.history.replaceState(null, "", "/reservierung/bestaetigung");
    }
  }, []);

  // Fallback: no state (page reload, deep-link from email). The
  // URL bar still shows the route, the state was never sent.
  if (
    !state ||
    typeof state.reservationNumber !== "string" ||
    typeof state.selfServiceUrl !== "string" ||
    typeof state.expiresAt !== "string"
  ) {
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
        data-testid="a7-confirmation-page"
      >
        <div
          className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4"
          data-testid="a7-fallback"
        >
          <h1
            className="font-heading text-2xl md:text-3xl mb-2"
            data-testid="a7-fallback-title"
          >
            {t("wizard.a7.fallbackTitle")}
          </h1>
          <p className="text-sm text-text/80" data-testid="a7-fallback-body">
            {t("wizard.a7.fallbackBody")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="a7-fallback-home"
            className="mt-4 min-h-[44px] px-4 py-2 rounded-md border border-accent text-accent-700 hover:bg-accent-100 inline-flex items-center gap-2"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            <span>{t("wizard.a7.home")}</span>
          </button>
        </div>
      </main>
    );
  }

  const isManual = state.confirmationMode === "manual";
  const expiresLabel = formatExpires(state.expiresAt, i18n.language);

  async function copyLink() {
    if (!state?.selfServiceUrl) return;
    if (typeof navigator === "undefined") return;
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(state.selfServiceUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard denied; the link is still visible+selectable */
    }
  }

  function newReservation() {
    resetWizard();
    navigate("/reservieren/datum");
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a7-confirmation-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="px-4 py-3 border-b border-divider"
          data-testid="a7-header"
        >
          <h1
            className="font-heading text-2xl md:text-3xl"
            data-testid="a7-heading"
          >
            {isManual ? t("wizard.a7.titleManual") : t("wizard.a7.title")}
          </h1>
        </header>

        <div className="px-4 pt-4">
          <p className="text-xs text-text/70 mb-2" data-testid="a7-meta">
            {isManual ? "Anfrage" : "Bestätigt"} ·{" "}
            {t("reservation.wizard.step6")}
          </p>
        </div>

        <div className="px-4 pb-4 space-y-3">
          <div data-testid="a7-reservation-number">
            <p className="text-xs text-text/70 uppercase tracking-wide">
              {t("wizard.a7.reservationNumberLabel")}
            </p>
            <p
              className="font-mono text-2xl tabular-nums mt-1"
              data-testid="a7-reservation-number-value"
            >
              {state.reservationNumber}
            </p>
          </div>

          <div data-testid="a7-self-service">
            <p className="text-xs text-text/70 uppercase tracking-wide">
              {t("wizard.a7.selfServiceLabel")}
            </p>
            <a
              href={state.selfServiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="a7-self-service-link"
              className="block mt-1 break-all text-sm text-accent-700 underline hover:text-accent"
            >
              {state.selfServiceUrl}
            </a>
            <button
              type="button"
              onClick={() => void copyLink()}
              data-testid="a7-copy-link"
              className="mt-2 inline-flex items-center gap-1 text-xs text-text/70 hover:text-text"
            >
              <Copy className="h-3 w-3" aria-hidden="true" />
              <span>
                {copied ? t("wizard.a7.linkCopied") : t("wizard.a7.copyLink")}
              </span>
            </button>
          </div>

          <p
            className="text-sm text-text/80"
            data-testid="a7-token-expires-caption"
          >
            {t("wizard.a7.tokenExpiresCaption")}
          </p>
          <p className="text-xs text-text/60" data-testid="a7-token-expires-at">
            {t("wizard.a7.tokenExpiresAt", { date: expiresLabel })}
          </p>
        </div>

        <div className="px-4 pb-4">
          <p
            className="text-sm text-text/80 mb-2"
            data-testid="a7-next-actions-heading"
          >
            {t("wizard.a7.nextActions")}
          </p>
          <div className="flex flex-col gap-2">
            <a
              href={state.selfServiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="a7-manage-action"
              className="min-h-[44px] px-4 py-2 rounded-md border border-accent text-accent-700 hover:bg-accent-100 inline-flex items-center justify-center gap-2"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              <span>{t("wizard.a7.manageAction")}</span>
            </a>
            <button
              type="button"
              onClick={newReservation}
              data-testid="a7-new-action"
              className="min-h-[44px] px-4 py-2 rounded-md border border-divider text-text hover:bg-surface inline-flex items-center justify-center gap-2"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              <span>{t("wizard.a7.newAction")}</span>
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

function formatExpires(iso: string, locale: string): string {
  if (typeof iso !== "string" || iso.length === 0) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return new Intl.DateTimeFormat(locale.startsWith("en") ? "en-US" : "de-DE", {
    dateStyle: "medium",
  }).format(dt);
}
